<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { IncidentSearchPeriod } from '@roadfootage/api-client'

import IncidentSearchBox from '../components/IncidentSearchBox.vue'
import IncidentSearchResultItem from '../components/IncidentSearchResultItem.vue'
import { useSearchStore } from '../stores/search'
import { formatNumber } from '../utils/format'
import { parseSearchQuery, toSearchQuery } from '../utils/search-route'

const $q = useQuasar()
const route = useRoute()
const router = useRouter()
const searchStore = useSearchStore()
const { results, totalCount, hasMore, isLoading, hasError, hasSearched } = storeToRefs(searchStore)

const state = computed(() => parseSearchQuery(route.query))
// Changes whenever the search itself changes, so the infinite scroller starts over.
const searchKey = computed(() => JSON.stringify(toSearchQuery(state.value)))

const periodOptions = [
  { label: 'Any time', value: IncidentSearchPeriod.Any },
  { label: 'Week', value: IncidentSearchPeriod.PastWeek },
  { label: 'Month', value: IncidentSearchPeriod.PastMonth },
  { label: 'Year', value: IncidentSearchPeriod.PastYear }
]

watch(searchKey, () => searchStore.search(state.value), { immediate: true })

function updateFilters(patch: Partial<{ hasFootage: boolean; period: IncidentSearchPeriod }>) {
  router.replace({ name: 'search', query: toSearchQuery({ ...state.value, ...patch }) })
}

function retry() {
  searchStore.search(state.value)
}

async function onLoad(_index: number, done: (stop?: boolean) => void) {
  await searchStore.loadMore()
  done(!hasMore.value || hasError.value)
}

const showSkeleton = computed(() => isLoading.value && results.value.length === 0)
const isEmpty = computed(
  () => hasSearched.value && !isLoading.value && !hasError.value && results.value.length === 0
)
</script>

<template>
  <q-page padding>
    <div class="search-page">
      <!-- The header carries the box from desktop width up; below that it lives here. -->
      <incident-search-box v-if="$q.screen.lt.md" autofocus class="q-mb-md" />

      <div class="row items-center q-col-gutter-sm q-mb-md">
        <div class="col-12 col-sm-auto">
          <q-toggle
            :model-value="state.hasFootage"
            label="Has footage"
            @update:model-value="(value: boolean) => updateFilters({ hasFootage: value })"
          />
        </div>
        <div class="col-12 col-sm-auto">
          <q-btn-toggle
            :model-value="state.period"
            :options="periodOptions"
            toggle-color="primary"
            no-caps
            dense
            unelevated
            outline
            aria-label="Time period"
            @update:model-value="(value: IncidentSearchPeriod) => updateFilters({ period: value })"
          />
        </div>
      </div>

      <div v-if="!state.q" class="text-center text-blue-grey-5 q-pa-xl">
        <q-icon name="mdi-magnify" size="4rem" class="q-mb-md" />
        <div class="text-h6">Search incidents</div>
        <div class="text-body2 q-mt-sm">Try a suburb, a road name or a type like "road rage".</div>
      </div>

      <div v-else-if="showSkeleton" class="column q-gutter-sm" aria-label="Loading results">
        <q-card v-for="n in 4" :key="n" flat bordered class="row no-wrap">
          <q-skeleton type="rect" style="flex: 0 0 42%; aspect-ratio: 16/9" />
          <div class="col q-pa-sm">
            <q-skeleton type="text" width="40%" />
            <q-skeleton type="text" />
            <q-skeleton type="text" width="60%" />
          </div>
        </q-card>
      </div>

      <div v-else-if="hasError && results.length === 0" class="text-center q-pa-xl">
        <q-icon name="mdi-alert-circle-outline" size="3rem" color="negative" class="q-mb-md" />
        <div class="text-h6">Search is not working right now</div>
        <q-btn flat color="primary" label="Try again" class="q-mt-sm" @click="retry" />
      </div>

      <div v-else-if="isEmpty" class="text-center text-blue-grey-5 q-pa-xl">
        <q-icon name="mdi-video-off-outline" size="4rem" class="q-mb-md" />
        <div class="text-h6">No incidents match "{{ state.q }}"</div>
        <div class="text-body2 q-mt-sm">Check the spelling or try fewer filters.</div>
      </div>

      <template v-else-if="results.length > 0">
        <div class="text-caption text-grey-7 q-mb-sm">
          {{ formatNumber(totalCount) }} {{ totalCount === 1 ? 'result' : 'results' }}
        </div>

        <q-infinite-scroll :key="searchKey" :offset="250" @load="onLoad">
          <div class="column q-gutter-sm">
            <incident-search-result-item
              v-for="incident in results"
              :key="incident.id"
              :incident="incident"
              @select="router.push({ name: 'preview', params: { incidentReportId: incident.id } })"
            />
          </div>

          <template #loading>
            <div class="row justify-center q-my-md">
              <q-spinner-dots color="primary" size="40px" />
            </div>
          </template>
        </q-infinite-scroll>
      </template>
    </div>
  </q-page>
</template>

<style scoped>
.search-page {
  max-width: 900px;
  margin: 0 auto;
}
</style>
